import * as THREE from 'three';
import gsap from 'gsap';
import { ContactSheet } from './components/ContactSheet.js';
import { NavigationController } from './components/NavigationController.js';
import { Router } from './components/Router.js';
import { HamburgerMenu } from './components/HamburgerMenu.js';
import { InfoModal } from './components/InfoModal.js';
import { calculateOverviewFrustum } from './components/SheetUtils.js';

// Prevent browser zoom (Cmd+/-, Ctrl+/-, trackpad pinch)
document.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && (e.key === '=' || e.key === '-' || e.key === '0')) {
        e.preventDefault();
    }
});
document.addEventListener('wheel', (e) => {
    if (e.ctrlKey || e.metaKey) e.preventDefault();
}, { passive: false });
document.addEventListener('gesturestart', (e) => e.preventDefault(), { passive: false });
document.addEventListener('gesturechange', (e) => e.preventDefault(), { passive: false });

// Make GSAP globally available (for compatibility with existing code that uses window.gsap)
window.gsap = gsap;

// Create scene with optimization flags
const scene = new THREE.Scene();
// Make scene background transparent to let CSS gradient show through
scene.background = null;

// Create camera
const { size: overviewFrustumSize, aspect } = calculateOverviewFrustum();
const halfHeight = overviewFrustumSize / 2;
const halfWidth = overviewFrustumSize * aspect / 2;

const camera = new THREE.OrthographicCamera(
    -halfWidth,
    halfWidth,
    halfHeight,
    -halfHeight,
    0.1,
    1000
);
camera.position.z = 5;

// Create optimized renderer
// iOS thermal/load optimization: disable MSAA on iOS only (no visual change intended)
const ua = navigator.userAgent || '';
const isIOS = /iPad|iPhone|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const renderer = new THREE.WebGLRenderer({
    antialias: isIOS ? false : true,
    alpha: true,
    powerPreference: 'high-performance',
    precision: 'mediump',
    stencil: false,
    depth: true
});
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
// Clear with transparent background to let CSS show through
renderer.setClearColor(0x000000, 0);

// Set correct color management
renderer.outputColorSpace = THREE.SRGBColorSpace;

document.body.appendChild(renderer.domElement);

// Canvas setup - let GestureManager handle all touch configuration
const canvas = renderer.domElement;

// Add loading overlay
const loadingOverlay = document.createElement('div');
loadingOverlay.id = 'loading-overlay';
loadingOverlay.style.position = 'fixed';
loadingOverlay.style.top = '0';
loadingOverlay.style.left = '0';
loadingOverlay.style.width = '100%';
loadingOverlay.style.height = '100%';
loadingOverlay.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
loadingOverlay.style.display = 'flex';
loadingOverlay.style.alignItems = 'center';
loadingOverlay.style.justifyContent = 'center';
loadingOverlay.style.zIndex = '1000';
loadingOverlay.style.fontFamily = '"IBM Plex Mono Light", "SF Mono", Menlo, Monaco, Consolas, monospace';
loadingOverlay.style.color = 'white';
loadingOverlay.innerHTML = `
    <div style="display: flex; flex-direction: column; align-items: center; gap: 30px;">
        <img src="/images/br-logo.svg" alt="BR Logo" style="width: 36px; height: auto; display: block;" id="loading-logo" />
        <div id="loading-text">Developing negatives…</div>
    </div>
`;
document.body.appendChild(loadingOverlay);

// Progress callback is driven by ContactSheet after each image mesh is
// added to the scene (not when the texture finishes decoding), so the
// counter stays in sync with what the user can actually see.

// Clear inline background styles to ensure CSS is used
document.body.style.backgroundColor = '';
document.body.style.margin = '0';
document.body.style.overflow = 'hidden';

// Helper function to show error messages to the user
function showErrorMessage(message) {
    const errorContainer = document.createElement('div');
    errorContainer.style.position = 'fixed';
    errorContainer.style.top = '50%';
    errorContainer.style.left = '50%';
    errorContainer.style.transform = 'translate(-50%, -50%)';
    errorContainer.style.background = 'rgba(0,0,0,0.8)';
    errorContainer.style.color = 'white';
    errorContainer.style.padding = '20px';
    errorContainer.style.borderRadius = '5px';
    errorContainer.style.zIndex = '9999';
    errorContainer.style.fontFamily = '"IBM Plex Mono Light", "SF Mono", Menlo, Monaco, Consolas, monospace';
    errorContainer.style.maxWidth = '80%';
    errorContainer.style.textAlign = 'center';
    errorContainer.textContent = message;
    
    // Add a refresh button
    const refreshButton = document.createElement('button');
    refreshButton.textContent = 'Refresh Page';
    refreshButton.style.marginTop = '15px';
    refreshButton.style.padding = '8px 16px';
    refreshButton.style.background = '#ffffff';
    refreshButton.style.border = 'none';
    refreshButton.style.borderRadius = '4px';
    refreshButton.style.cursor = 'pointer';
    refreshButton.style.fontFamily = '"IBM Plex Mono Light", "SF Mono", Menlo, Monaco, Consolas, monospace';
    refreshButton.onclick = () => window.location.reload();
    
    errorContainer.appendChild(document.createElement('br'));
    errorContainer.appendChild(refreshButton);
    
    document.body.appendChild(errorContainer);
}

// Initialize contact sheet and NavigationController
let contactSheet;
let navigationController;
let router; // Step 1.2: Prepare Router instance
let hamburgerMenu;
let infoModal;
try {
    contactSheet = new ContactSheet(scene, camera, 'sosf');
    navigationController = new NavigationController();
    if (Router.LOGGING_ENABLED) console.log('NavigationController contract verified.');
    router = new Router(navigationController); // Step 1.2: Instantiate Router (no integration yet)
    hamburgerMenu = new HamburgerMenu(navigationController);
    infoModal = new InfoModal(navigationController);
    window.router = router; // Expose for manual testing
    window.hamburgerMenu = hamburgerMenu; // Expose for debugging
    
    scene.renderer = renderer;
    contactSheet.init().then(() => {
        console.log('ContactSheet: Initialization complete');
        console.log('Router: Initialization deferred until components ready');
        if (Router.LOGGING_ENABLED) console.log('All UI/data ready. Router initialization proceeding.');
        // Wire NavigationController after ContactSheet is initialized
        navigationController.setComponents(contactSheet, contactSheet.detailView, contactSheet.gestureManager);
        navigationController.setHamburgerMenu(hamburgerMenu);
        navigationController.setInfoModal(infoModal);
        contactSheet.setNavigationController(navigationController);
        contactSheet.detailView.setNavigationController(navigationController);
        // Set contact sheet reference in detail view for close button control
        contactSheet.detailView.setContactSheet(contactSheet);
        navigationController.setupEventHandlers();
        // Mark NavigationController as ready AFTER ContactSheet is fully initialized
        navigationController.markAsReady();
        // Initialize the router now that all components are ready
        router.init();
        
        // Remove loading overlay
        if (loadingOverlay) {
            loadingOverlay.style.opacity = '0';
            loadingOverlay.style.transition = 'opacity 0.3s';
            setTimeout(() => loadingOverlay.remove(), 300);
        }
    }).catch(error => {
        console.error('Error initializing contact sheet:', error);
        // Remove loading overlay on error
        if (loadingOverlay) loadingOverlay.remove();
        // Don't show error message for individual image load failures
        if (error && error.type !== 'error') {
            showErrorMessage('Unable to load content. This may be due to a connection issue or browser compatibility problem. Please try again or use a different browser.');
        }
    });
} catch (error) {
    console.error('Error creating contact sheet:', error);
    // Remove loading overlay on error
    const overlay = document.getElementById('loading-overlay');
    if (overlay) overlay.remove();
    showErrorMessage('Unable to load content. This may be due to a connection issue or browser compatibility problem. Please try again or use a different browser.');
}

// Optimize resize handler with throttling
let resizeTimeout;
const throttledResize = () => {
    if (resizeTimeout) return;
    
    resizeTimeout = setTimeout(() => {
        // Let the contact sheet handle the camera aspects
        if (contactSheet) {
            contactSheet.handleResize();
        } else {
            // Fallback if contact sheet isn't initialized yet
            const { size: overviewFrustumSize, aspect } = calculateOverviewFrustum();
            const halfHeight = overviewFrustumSize / 2;
            const halfWidth = overviewFrustumSize * aspect / 2;
            
            camera.left = -halfWidth;
            camera.right = halfWidth;
            camera.top = halfHeight;
            camera.bottom = -halfHeight;
            camera.updateProjectionMatrix();
        }
        
        // Resize renderer
        renderer.setSize(window.innerWidth, window.innerHeight);
        
        resizeTimeout = null;
    }, 100);
};

window.addEventListener('resize', throttledResize);

// Render loop synced to renderer/GSAP tick without manual throttling
function render() {
    renderer.render(scene, camera);
}
renderer.setAnimationLoop(render);

// Handle visibility changes to pause/resume animations
function onVisibilityChange() {
    if (document.hidden) {
        // Pause render loop when tab is not visible
        renderer.setAnimationLoop(null);
    } else {
        // Resume render loop when tab becomes visible again
        renderer.setAnimationLoop(render);
        // Trigger resize check when returning to tab to handle window resizes that occurred while hidden
        throttledResize();
    }
}

// Add event listeners with proper cleanup
document.addEventListener('visibilitychange', onVisibilityChange);
